import { useState, useEffect } from "react";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = NAV_LINKS.map((l) => l.href.replace("#", ""));
      let current = "home";
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 120) current = id;
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      {/* Top utility bar */}
      <div className="bg-charcoal-950 border-b border-charcoal-800/60 py-1.5 px-4 md:px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-charcoal-200 text-xs hidden sm:block">
            Professional Accounting Services — Nationwide &amp; Remote
          </p>
          <div className="flex items-center gap-2.5 md:gap-3 w-full sm:w-auto justify-center sm:justify-end sm:ml-0">
            <a
              href="tel:+17864612818"
              className="flex items-center gap-1.5 text-charcoal-200 hover:text-white text-xs transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-forest-400" />
              786-461-2818
            </a>
            <span className="text-charcoal-600">|</span>
            <a
              href="tel:+13057332170"
              className="flex items-center gap-1.5 text-charcoal-200 hover:text-white text-xs transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-forest-400" />
              305-733-2170
            </a>
            <a
              href="mailto:admin@mtcassociate.com"
              className="hidden md:flex items-center gap-1.5 text-charcoal-200 hover:text-white text-xs transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-forest-400" />
              admin@mtcassociate.com
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="bg-charcoal-900 py-3 md:py-4">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => handleNav(e, "#home")}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/01f647ac-7c86-4957-a691-a312488d1835/b834e0b9-5147-41e6-9283-ddaf681edf2e_Gemini_Generated_Image_we2h8lwe2h8lwe2h.png?v=7910b91963806043e5a40ee3ce67ff55"
              alt="MTC Associates Logo"
              className="h-12 w-auto object-contain transition-all duration-300"
            />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNav(e, link.href)}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  activeSection === link.href.replace("#", "")
                    ? "text-forest-300"
                    : "text-charcoal-200 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* CTA */}
            <a
              href="#contact"
              onClick={(e) => handleNav(e, "#contact")}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap bg-forest-600 hover:bg-forest-700 text-white"
            >
              Free Consultation
            </a>

            {/* Mobile toggle */}
            <button
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer text-white"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-charcoal-900 border-t border-charcoal-700 px-4 py-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className="block py-3 text-sm font-medium text-charcoal-200 hover:text-white transition-colors border-b border-charcoal-700/50 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNav(e, "#contact")}
            className="mt-4 block text-center bg-forest-600 text-white px-5 py-3 rounded-md text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            Free Consultation
          </a>
        </div>
      )}
    </header>
  );
}
